import React, { useCallback, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import type { RejectedReasonModalProps } from "@/features/dmManagement/types";

/**
 * 上下シャドウの要否を判定する。本文がその向きにはみ出している時だけ true。
 * スクロール量の端数で誤検知しないよう 1px の余裕を持たせる。
 */
export const computeScrollShadow = (
  scrollTop: number,
  clientHeight: number,
  scrollHeight: number
): { top: boolean; bottom: boolean } => ({
  top: scrollTop > 1,
  bottom: scrollTop + clientHeight < scrollHeight - 1,
});

/**
 * デザイン申請の却下理由（ラクスル側が登録）を全文表示するモーダル。
 */
export const RejectedReasonModal: React.FC<RejectedReasonModalProps> = ({
  open,
  onClose,
  reason,
}) => {
  // 想定外に null/undefined が渡っても文字列メソッド呼び出しで落ちないよう ?? "" でガードする。
  const displayText = (reason ?? "").trim() || "却下理由：不明";

  const [shadow, setShadow] = useState({ top: false, bottom: false });

  const measure = useCallback((element: HTMLElement) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    const next = computeScrollShadow(scrollTop, clientHeight, scrollHeight);
    // スクロール中に高頻度で発火するため、値が変わった時だけ更新して再レンダリングを抑える。
    setShadow((prev) =>
      prev.top === next.top && prev.bottom === next.bottom ? prev : next
    );
  }, []);

  // ResizeObserver は監視開始直後（高さ 0→実寸の確定時）にも発火するため、開いた直後や
  // リサイズ時にも影の要否を測り直せる（onScroll だけでは初期状態を取りこぼす）。
  const observerRef = useRef<ResizeObserver | null>(null);
  const setContentRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!element) return;
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(() => {
          measure(element);
        });
        observer.observe(element);
        observerRef.current = observer;
      }
      measure(element);
    },
    [measure]
  );

  // CustomModal は backdropClick / escapeKeyDown 時だけこれを呼ぶ。
  // 背面クリックや Esc では閉じず「閉じる」ボタンからのみ閉じたいので、ここでは何もしない。
  const handleModalClose = (
    _event: Record<string, unknown>,
    _reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    // 意図的に何もしない（背面クリック・Esc では閉じない）
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleModalClose}
      ariaLabelledby="rejected-reason-modal"
      ariaDescribedby="rejected-reason-modal-description"
      width={{ xs: "90vw", sm: "500px", md: "600px", lg: "720px" }}
      maxHeight={"70vh"}
      overflow="hidden"
    >
      {/* 上限は 70vh から CustomModal の上下パディング p:4（32px×2）を引いた値 */}
      <Stack
        direction={"column"}
        spacing={3}
        sx={{ maxHeight: "calc(70vh - 64px)" }}
      >
        <Typography
          id="rejected-reason-modal"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 700 }}
        >
          却下理由
        </Typography>
        {/* macOS 等のオーバーレイ式スクロールバーは隠れて続きの有無が伝わらないため、
            はみ出している向きにだけ影を描いて「続きがある」ことを示す。 */}
        <Box
          ref={setContentRef}
          onScroll={(e) => {
            measure(e.currentTarget);
          }}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            background:
              [
                shadow.top &&
                  "radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0)) center top",
                shadow.bottom &&
                  "radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0)) center bottom",
              ]
                .filter(Boolean)
                .join(", ") || "none",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 18px, 100% 18px",
          }}
        >
          <Typography
            id="rejected-reason-modal-description"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {displayText}
          </Typography>
        </Box>
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent={"center"}
          sx={{ flexShrink: 0 }}
        >
          <Button
            label="閉じる"
            onClick={onClose}
            size={SizingWrapperStyle.INHERIT}
            variant={ButtonVariantOption.Outlined}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
};
